import { useEffect, useState } from "react";
import { useRoute, Link } from "wouter";
import { ArrowLeft, Share2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BlogLikeButton } from "../components/BlogLikeButton";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { BlogStoryCard, BlogShareDialog, blogDate, type BlogCard } from "../components/BlogStoryCard";
import "./Blogs.css";

/** The CMS stores plain text with heading prefixes; never interpret it as HTML. */
function Body({ text }: { text: string }) {
  return <div className="bp-article-body">{text.split(/\n{2,}/).map(block => block.trim()).filter(Boolean).map((block, index) => {
    const [firstLine, ...remainingLines] = block.split("\n");
    const heading = firstLine.startsWith("## ") ? <h3>{firstLine.slice(3)}</h3>
      : firstLine.startsWith("# ") ? <h2>{firstLine.slice(2)}</h2> : null;
    if (heading) return <div key={index}>{heading}{remainingLines.length > 0 && <p>{remainingLines.join("\n")}</p>}</div>;
    return <p key={index}>{block}</p>;
  })}</div>;
}

/** Sources retain the CMS's label|url format, allowing only public HTTP(S) links. */
function Sources({ raw }: { raw: string | null }) {
  const lines = (raw || "").split("\n").map(line => line.trim()).filter(Boolean);
  return <section className="bp-article-sources" aria-label="Sources"><h2>Sources</h2>{lines.length > 0 ? <ol>
    {lines.map((line, index) => {
      const [label, link] = line.split("|").map(part => part.trim());
      const url = link || line;
      return <li key={index}>{/^https?:\/\//i.test(url)
        ? <a href={url} target="_blank" rel="noreferrer noopener">{link ? label : line}</a> : line}</li>;
    })}
  </ol> : <p className="text-slate-500">No sources provided.</p>}</section>;
}

export default function BlogPost() {
  const { user, loading } = useAuth();
  const [, params] = useRoute("/blogs/:slug");
  const slug = params?.slug || "";
  const [post, setPost] = useState<Awaited<ReturnType<typeof api.getBlogPost>>["post"] | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");
  const [related, setRelated] = useState<BlogCard[]>([]);
  const [sharing, setSharing] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (loading) return;
    let alive = true;
    setState("loading");
    setSharing(false);
    setRelated([]);
    api.getBlogPost(slug).then(response => {
      if (!alive) return;
      if (!response.post) { setState("notfound"); return; }
      setPost(response.post);
      setState("ready");
      api.listBlog().then(list => {
        if (alive) setRelated(list.posts.filter(candidate => candidate.id !== response.post.id && candidate.category === response.post.category).slice(0, 3));
      }).catch(() => { /* Related stories are optional; the requested article stays available. */ });
    }).catch((error: unknown) => {
      if (alive) setState(error instanceof Error && "status" in error && error.status === 404 ? "notfound" : "error");
    });
    return () => { alive = false; };
  }, [slug, attempt, loading, user?.id]);

  if (state === "loading") return <DetailLoading label="Loading article…" />;
  if (state === "notfound") return <DetailNotFound title="Article not found" backHref="/blogs" backLabel="Back to stories" />;
  if (state === "error" || !post) return <div className="blogs-page bp-empty" role="alert"><h1>Could not load this story</h1><p>Please try again.</p><button onClick={() => setAttempt(value => value + 1)}>Try again</button><p><Link href="/blogs">Back to stories</Link></p></div>;

  const readingMinutes = Math.max(1, Math.ceil(post.body.trim().split(/\s+/).filter(Boolean).length / 200));
  return <div className="blogs-page bp-article-page"><article className="bp-article">
    <Link href="/blogs" className="bp-article-back"><ArrowLeft size={16} aria-hidden="true" />Back to stories</Link>
    <img className="bp-article-cover" src={post.coverImage || "/hero/professional-services.jpg"} alt="" />
    <div className="bp-article-inner">
      <div className="bp-article-toolbar"><span className="bp-article-category">{post.category || "Community"}</span>
        <div className="bp-social-actions"><BlogLikeButton key={post.id} post={post} />
          <button className="bp-social-action" onClick={() => setSharing(true)} aria-label={`Share ${post.title}`}><Share2 size={18} aria-hidden="true" /><span>Share</span></button>
        </div>
      </div>
      <h1>{post.title}</h1>
      <div className="bp-article-meta"><p>Author: <strong>{post.authorName || "KontrataPH Editorial"}</strong></p>
        <p>{blogDate(post.publishedAt, true)} · {readingMinutes} min read</p>
      </div>
      {post.excerpt && <p className="bp-article-excerpt">{post.excerpt}</p>}
      <Body text={post.body} />
      {post.tags.length > 0 && <div className="bp-preview-tags">{post.tags.map(tag => <span key={tag}>{tag}</span>)}</div>}
      <Sources raw={post.sources} />
      {related.length > 0 && <section className="bp-related" aria-labelledby="blog-related-heading">
        <span className="bp-eyebrow">KEEP EXPLORING</span><h2 id="blog-related-heading">Related stories</h2>
        <div className="bp-grid">{related.map(story => <BlogStoryCard key={story.id} post={story} />)}</div>
      </section>}
    </div>
  </article>{sharing && <BlogShareDialog post={post} onClose={() => setSharing(false)} />}</div>;
}
